/** @format */

// vendor
import { Ref, SetupContext, reactive, watch } from "@vue/composition-api";
// project
import { ContextOption } from "@/components.generic/context-menu";
import { useFavourites } from "@/hooks/useFavourites";
import { useTags } from "@/hooks/useTags";
import { useAssets } from "@/hooks/useAssets";
import { Asset } from "@/psychlab/types/assets";
import * as AppModal from "@/AppModal";
import { pascalToSentenceCase } from "@/utils/text";


const initPagination = (prefix:string, ctx:SetupContext) => {
	const qPage = getQueryValue(`${prefix}_page`, ctx, 1);
	const qLimit = getQueryValue(`${prefix}_limit`, ctx, 10);
	return reactive({
		page:Number(qPage),
		limit:Number(qLimit),
		total:0
	});
}

type Config = {
	activeTag:Ref<string|null>;
	context:SetupContext;
	queryPrefix?:string;
	contextFn?:(a:Asset) => ContextOption[];
};

export const useAssetListDisplay = (c:Config) => {

	const assetHooks = useAssets();

	const {
		assets
	} = assetHooks;

	const {
		untagItem,
		hasTag,
		sortedTags,
	} = useTags();

	const {
		isFavourite,
		addFavourite,
		removeFavourite
	} = useFavourites();

	const {
		activeTag,
		contextFn,
	} = c;

	const pagination = initPagination(c.queryPrefix || "", c.context);

	let initialPage = pagination.page;

	pagination.total = assets.value.length;
	
	watch(() => pagination.total, v => {
		if(initialPage >= 0){
			pagination.page = initialPage;
			initialPage = -1;
		}
	});

	watch(() => assets.value.length, v => pagination.total = v);

	watch(() => pagination.limit, v => setQueryValue(`${c.queryPrefix || ''}_limit`, v, c.context));

	watch(() => pagination.page, v => setQueryValue(`${c.queryPrefix || ''}_page`, v, c.context));


	const findAsset = (id:string) => {
		return assets.value.find(a => a._id === id);
	};

	const getAssetTags = (assetId:string) => {
		return sortedTags.value.filter(t => hasTag(t._id, assetId));
	};

	const renameAsset = (id:string) => {
		const a = findAsset(id);

		if(!a){ return; }

		const title = `Rename ${a.type === "Graph" ? "Blueprint" : pascalToSentenceCase(a.type)}`
		
		AppModal.editText({
			value: a.name,
			title,
			confirmText:"Save",
			validator(v){
				return v.length > 2 && v !== a.name;
			}
		},
		(newName:string) => {
			assetHooks.renameAsset(a._id, newName);
		});
	};

	const deleteAsset = (id:string) => {
		const a = findAsset(id);
		if(!a){ return; }
		AppModal.confirm({
			title: "Delete",
			variant: "danger",
			description: "This is irreversible. Are you sure?",
			okText: "I Understand",
		}, async () => {
			assetHooks.deleteAsset(a._id);
		});
	};

	const duplicateAsset = (id:string) => {
		const a = findAsset(id);
		if(!a){ return; }
		AppModal.editText({
			title:"Duplicate",
			value: `Copy of ${a.name}`,
			validator(){
				return true;
			},
			confirmText:"Submit"
		},
		async (newName:string) => {
			assetHooks.duplicateAsset(a._id, newName, activeTag.value);
		});
	};

	const editAsset = (id:string) => {
		const ctx = c.context;
		if(!ctx){ return; }
		ctx.root.$router.push({
			name:"asset.edit",
			params:{
				assetId:id,
			}
		});
	};

	const viewAssetHistory = (id:string) => {
		const ctx = c.context;
		if(!ctx){ return; }
		ctx.root.$router.push({
			name:"asset-history",
			params:{
				assetId:id,
			}
		});
	};

	const openAssetContext = (e:Event, assetId:string) => {

		const a = assets.value.find(aa => aa._id === assetId);
		if(!a){ return; }
		
		const options = [
			...(contextFn ? contextFn(a) : [])
		];

		if(c.context){
			options.push({
				name:"Edit",
				icon:"mdi.pencil",
				fn:() => {
					editAsset(assetId);
				}
			});
		}
		
		options.push({
			name:"View History",
			icon:"mdi.history",
			fn:() => {
				viewAssetHistory(assetId);
			}
		});

		if(!isFavourite(assetId)){
			options.push({
				name:"Add to Favourites",
				icon:"mdi.star",
				order:4,
				fn:() => addFavourite(assetId, "asset")
			});
		}
		else {
			options.push({
				name:"Remove Favourite",
				icon:"mdi.star-outline",
				order:4,
				fn:() => removeFavourite(assetId)
			});
		}

		options.push({
			name:"Rename",
			icon:"mdi.pencil-box",
			order:1,
			fn:() => renameAsset(assetId)
		});

		options.push({
			name:"Duplicate",
			icon:"mdi.content-copy",
			order:1,
			fn:() => duplicateAsset(assetId)
		});

		if(activeTag.value && activeTag.value !== "all"){

			const tag = activeTag.value;

			options.push({
				name:"Remove Tag",
				icon:"mdi.tag-remove",
				order:4,
				fn:() => {
					untagItem(tag, assetId);
				}
			});
		}

		options.push({
			name:"Delete",
			icon:"mdi.trash-can",
			order:5,
			fn:() => deleteAsset(assetId)
		});

		AppModal.context(e, options);
	};

	return {
		pagination,
		openAssetContext,
		getAssetTags,
	};
};



export const setQueryValue = (k:string, v:any, context:SetupContext) => {
	const q:any = { ...context.root.$route.query };
	if(v !== undefined && v !== null){
		q[k] = v;
	}
	else {
		delete q[k];
	}
	context.root.$router.push({ query: q})
	.catch(() => {});
};


export const getQueryValue = <T>(k:string, context:SetupContext, def:T):T => {
	return ((context.root.$route.query[k] as any) as T) || def;
};